/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { CarbonDetailSiteField, KPI_PERCENTAGE_DECIMAL_PRECISION } from '../../../shared/Utils/Constants';
import { IDataTableColumn } from '../../../shared/MeterSavingsDetailTable/IMeterSavingsDetailTableProps';
import { getSavingsBadgeColor } from '../../../shared/Utils/CarbonViewDetailsUtils';
import { ICarbonAvoidanceSummaryComponentProps } from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import CommonProgressBar from '../../../shared/CommonProgressBar/CommonProgressBar';
import { getAbsoluteValue } from '../../../shared/Utils/Utils';
import { DataTable } from '@scuf/datatable';
import { ConversionType, getConvertedValue, getMaxConvertedValueFromArray } from '../../../../main';
import { getConvertedValues, getNullToZero } from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import { getAvoidanceTotalAggregationValues } from '../../../shared/Utils/unitHandling/EmaasSiteKpiDataScaling';
export const CarbonAvoidanceSummaryComponent = (
    props: ICarbonAvoidanceSummaryComponentProps
) => {
    const [t] = useTranslation();
    const d = descriptors.DetailedView;
    const { data: { emissionMeasurementUnit } } = props;

    const valuesArray = getAvoidanceTotalAggregationValues([props?.data?.totalAggregations]);
    const maxValueUnitConversion = getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        emissionMeasurementUnit,
        valuesArray
    );

    interface ISiteSavingsData {
        source?: string;
        utility?: string;
        actualConsumption: number | null;
        baselineConsumption: number | null;
        change: number | null;
        percentageConsumptionSaving: number;
    }

    const baselineAvoidanceCost = getConvertedValue(
        ConversionType.CARBON,
        emissionMeasurementUnit,
        props?.data?.totalAggregations?.totalPreviousPeriodAvoidance ?? 0,
        maxValueUnitConversion.Unit
    ).Value;
    const actualAvoidanceCost = getConvertedValue(
        ConversionType.CARBON,
        emissionMeasurementUnit,
        props?.data?.totalAggregations?.totalActualAvoidance ?? 0,
        maxValueUnitConversion.Unit
    ).Value;
    const avoidanceCostSavings = getConvertedValue(
        ConversionType.CARBON,
        emissionMeasurementUnit,
        props?.data?.totalAggregations?.totalDeviation ?? 0,
        maxValueUnitConversion.Unit
    ).Value;
    const avoidanceCostSavingsPercentage = parseFloat(
        (props?.data?.totalAggregations?.totalDeviationPercentage ?? 0).toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
    );
    const formattedAvoidanceSavingsPercentage = parseFloat(
        getNullToZero(avoidanceCostSavingsPercentage)?.toFixed(
            KPI_PERCENTAGE_DECIMAL_PRECISION
        )
    );
    const values: ISiteSavingsData[] = [{
        source: props.data.energySource,
        utility: props.data.utilityType,
        actualConsumption: actualAvoidanceCost,
        baselineConsumption: baselineAvoidanceCost,
        change: avoidanceCostSavings,
        percentageConsumptionSaving: formattedAvoidanceSavingsPercentage
    }]
    const columnDetails: IDataTableColumn[] = [
        {
            className: 'datatable-column-meter',
            field: 'source',
            header: t(d.SOURCE),
            sortable: false,
            initialWidth: '7%'
        },
        {
            className: 'datatable-column-actualConsumption',
            field: 'utility',
            header: t(d.UTILITYTYPE),
            sortable: false,
            initialWidth: '9%'
        },
        {
            className: 'datatable-column-baselineConsumption',
            field: CarbonDetailSiteField.actualConsumption,
            header: t(d.CURRENTAVOIDANCE),
            sortable: false,
            initialWidth: '20%'
        },
        {
            className: 'datatable-column-baselineConsumption',
            field: CarbonDetailSiteField.baselineConsumption,
            header: t(d.PREVIOUSAVOIDANCE),
            sortable: false,
            initialWidth: '10%'
        },
        {
            className: 'datatable-column-change',
            field: CarbonDetailSiteField.change,
            header: t(d.CHANGE),
            sortable: false,
            initialWidth: '12%'
        }
    ];
    const cellDataRenderer = (cellData: ICellData) => {
        const { value, field, rowData } = cellData;
        if (field === 'source') {
            return (
                <span className="score-category-meterdata-meter">
                    <div className="consumptionvalue">{value}</div>
                </span>
            );
        }
        if (field === 'utility') {
            return (
                <span className="score-category-meterdata-meter">
                    <div className="consumptionvalue">{value}</div>
                </span>
            );
        }
        if (field === CarbonDetailSiteField.actualConsumption) {
            return (
                <span className="emaas-carbondetail-category-meterdata">
                    <span className="score-category-percent">
                        <div className="measurementunit">{getConvertedValues(value)}</div>
                        {maxValueUnitConversion.Unit}
                    </span>
                    <span className="score-category-progress">
                        <CommonProgressBar
                            actual={rowData.actualConsumption}
                            previousPeriod={rowData.baselineConsumption}
                            savings={rowData.change}
                            savingsPercentage={rowData.percentageConsumptionSaving}
                            progessBarRelativeWidth={rowData.progessBarRelativeWidth}
                        />
                    </span>
                </span>
            );
        }
        if (field === CarbonDetailSiteField.change) {
            return (
                <span className="score-category-meterdata-meter">
                    <span
                        className="savings-badge"
                        style={{
                            color: getSavingsBadgeColor(value)
                        }}
                    >
                        {getConvertedValues(value)}
                        {' ' +
                            maxValueUnitConversion.Unit +
                            '(' +
                            getAbsoluteValue(rowData.percentageConsumptionSaving) +
                            '%' +
                            ')'}
                    </span>
                </span>
            );
        }
        if (field === CarbonDetailSiteField.baselineConsumption) {
            return (
                <span className="score-category-meterdata-meter">
                    <div className="consumptionvalue">{getConvertedValues(value)}</div>
                    {maxValueUnitConversion.Unit}
                </span>
            );
        }
        return <></>;
    };
    return (
        <div className="emaas-Metersaving-details-head">
            <DataTable
                className="Savingtable"
                data={values}
            >
                {columnDetails.map((column: IDataTableColumn) => {
                    return (
                        <DataTable.Column
                            field={column.field}
                            header={column.header}
                            renderer={cellDataRenderer}
                            className={column.className}
                            initialWidth={column.initialWidth}
                        />
                    );
                })}
            </DataTable>
        </div>
    );
};
export default CarbonAvoidanceSummaryComponent;
